/**
 * A game's sign-up list, kept up to date: requested when mounted, then
 * followed through the live added / removed pushes and any later full list
 * (e.g. after an import).
 */

import { useEffect, useState } from 'react';
import { useSocketContext } from '@swtorbit/shared-react-hooks';
import {
  SocketEvent,
  type Registration,
  type RegistrationAddedBroadcast,
  type RegistrationRemovedBroadcast,
  type RegistrationsDataResponse,
  type RegistrationsListRequest,
} from '@swtorbit/shared-socket-events';

export function useRegistrations(params: {
  /** Database id of the game; the list is requested by it. */
  gameId: number;
  roomCode: string;
  /** An archived game is outside the live engine: no live pushes are for it. */
  archived?: boolean;
}): Registration[] {
  const { gameId, roomCode, archived = false } = params;
  const { socket } = useSocketContext();
  const [registrations, setRegistrations] = useState<Registration[]>([]);

  useEffect(() => {
    if (!socket) return;
    socket.emit(SocketEvent.REGISTRATIONS_LIST, { gameId } satisfies RegistrationsListRequest);

    // Replies to our requests carry gameId; live pushes carry only the room
    // code, which an archived game may share with the game holding it now.
    const isForThisGame = (data: { roomCode?: string; gameId?: number }) =>
      data.gameId !== undefined ? data.gameId === gameId : !archived && data.roomCode === roomCode;

    const handleData = (data: RegistrationsDataResponse) => {
      if (!isForThisGame(data)) return;
      setRegistrations(data.registrations ?? []);
    };
    const handleAdded = (data: RegistrationAddedBroadcast) => {
      if (!isForThisGame(data)) return;
      setRegistrations((prev) => {
        const filtered = prev.filter((r) => r.id !== data.registration.id);
        return [...filtered, data.registration].sort((a, b) => a.createdAt - b.createdAt);
      });
    };
    const handleRemoved = (data: RegistrationRemovedBroadcast) => {
      if (!isForThisGame(data)) return;
      setRegistrations((prev) => prev.filter((r) => r.id !== data.registrationId));
    };

    socket.on(SocketEvent.REGISTRATIONS_DATA, handleData);
    socket.on(SocketEvent.REGISTRATION_ADDED, handleAdded);
    socket.on(SocketEvent.REGISTRATION_REMOVED, handleRemoved);
    return () => {
      socket.off(SocketEvent.REGISTRATIONS_DATA, handleData);
      socket.off(SocketEvent.REGISTRATION_ADDED, handleAdded);
      socket.off(SocketEvent.REGISTRATION_REMOVED, handleRemoved);
    };
  }, [socket, gameId, roomCode, archived]);

  return registrations;
}
